import { ArrowRight } from "lucide-react";

import { track } from "@/lib/analytics";
import { ResponsiveImage } from "./ResponsiveImage";
import { CATEGORIAS_PRODUCTO, type Producto } from "@/lib/productos";

export function CatalogoCard({
  producto,
  headingLevel = 3,
}: {
  producto: Producto;
  headingLevel?: 2 | 3;
}) {
  const Heading = headingLevel === 2 ? "h2" : "h3";
  const categoria = CATEGORIAS_PRODUCTO.find((item) => item.id === producto.category);
  const escalaImagen =
    producto.imageZoom === "keychain"
      ? "scale-[1.45] group-hover:scale-[1.5]"
      : producto.imageZoom === "medium"
        ? "scale-[1.1] group-hover:scale-[1.14]"
        : producto.imageFit === "cover"
          ? "group-hover:scale-[1.035]"
          : "group-hover:scale-[1.02]";

  return (
    <a
      href={`/productos/${producto.id}`}
      onClick={() => track("product_cta_click", { producto: producto.name, ubicacion: "catalogo" })}
      className="group block h-full rounded-[1.25rem] focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary sm:rounded-[2rem]"
    >
      <article className="pv-product-card flex h-full flex-col overflow-hidden rounded-[1.25rem] bg-white sm:rounded-[2rem]">
        <div
          className={`pv-product-media relative aspect-square overflow-hidden sm:aspect-[4/5] ${producto.imageSurface ?? "bg-mist"}`}
        >
          <ResponsiveImage
            sizes="(max-width: 639px) 45vw, (max-width: 1023px) 40vw, 340px"
            src={producto.image}
            width={1200}
            height={1200}
            loading="lazy"
            alt={producto.name}
            className={`absolute inset-0 h-full w-full object-center transition-transform duration-700 ${
              producto.imageFit === "cover" ? "object-cover" : "object-contain"
            } ${escalaImagen}`}
          />
          {producto.featured && (
            <span className="pv-featured-badge absolute left-2 top-2 rounded-full px-2 py-1 font-mono text-[0.52rem] font-medium uppercase tracking-[0.08em] text-ink sm:left-4 sm:top-4 sm:px-3.5 sm:py-2 sm:text-[0.65rem] sm:tracking-[0.12em]">
              Más vendido
            </span>
          )}
        </div>
        <div className="pv-product-body flex flex-1 flex-col p-3 sm:p-6">
          <div className="hidden items-center justify-between gap-3 sm:flex">
            <span className="font-mono text-[0.65rem] uppercase tracking-[0.12em] text-slate">
              {categoria?.shortLabel}
            </span>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate">
              <span
                className={`h-2 w-2 rounded-full ${
                  producto.availability === "disponible" ? "bg-[#20bd5a]" : "bg-signal"
                }`}
              />
              {producto.availability === "disponible" ? "Disponible" : "A pedido"}
            </span>
          </div>
          <Heading className="text-base font-extrabold uppercase leading-[0.95] text-ink sm:mt-4 sm:text-3xl">
            {producto.name}
          </Heading>
          <p className="mt-4 hidden flex-1 text-base leading-relaxed text-slate sm:block">
            {producto.description}
          </p>
          <div className="mt-3 flex flex-1 flex-col justify-end border-t border-ink/10 pt-3 sm:mt-6 sm:block sm:pt-5">
            <p className="hidden font-mono text-[0.65rem] uppercase tracking-[0.14em] text-slate sm:block">
              Precio
            </p>
            <p className="text-base font-extrabold text-primary sm:mt-1 sm:text-2xl">
              {producto.price} <span className="hidden sm:inline">CLP</span>
            </p>
            <span className="pv-product-cta mt-3 inline-flex min-h-9 w-full items-center justify-center gap-1 rounded-lg px-2 py-2 text-center text-[0.7rem] font-bold text-white sm:mt-5 sm:min-h-14 sm:gap-2 sm:rounded-full sm:px-5 sm:py-3.5 sm:text-base">
              Cotizar <ArrowRight className="h-3.5 w-3.5 sm:h-5 sm:w-5" aria-hidden="true" />
            </span>
          </div>
        </div>
      </article>
    </a>
  );
}
